import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCopy, FaExpand } from 'react-icons/fa';
import { useToast } from '../../../../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../../../../U3121';
import type { U3500DocumentMeta } from '../../../../../u3500GedDocumentSearch/U3500Service';

export interface U3121ProjectDocumentPreviewPanelProps {
  mode: 'idle' | 'loading' | 'pdf' | 'local' | 'error';
  pdfObjectUrl: string | null;
  meta: U3500DocumentMeta | null;
  fileName: string | null;
  errorMessage: string | null;
  /** Pendant resize du split : désactive les events iframe (BL-0487). */
  disableIframePointerEvents?: boolean;
  /** Ouvre le dialog Agrandir (BL-0488) — visible seulement en mode PDF. */
  onExpandPreview?: () => void;
}

const U3121ProjectDocumentPreviewPanel: React.FC<U3121ProjectDocumentPreviewPanelProps> = ({
  mode,
  pdfObjectUrl,
  meta,
  fileName,
  errorMessage,
  disableIframePointerEvents = false,
  onExpandPreview,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCopied(false);
  }, [meta?.documentId, meta?.uncPath]);

  const handleCopyUnc = useCallback(async () => {
    const path = meta?.uncPath?.trim();
    if (!path) {
      return;
    }
    try {
      await navigator.clipboard.writeText(path);
      setCopied(true);
      toast.success(t('msgUncPathCopied', { ns: NS }));
    } catch {
      toast.error(t('msgUncPathCopyFailed', { ns: NS }), t('errorTitle', { ns: NS }));
    }
  }, [meta?.uncPath, t, toast]);

  if (mode === 'idle') {
    return (
      <div className="flex h-full min-h-[800px] items-center justify-center p-4 text-sm text-gray-500">
        {t('msgDocumentSelectFile', { ns: NS })}
      </div>
    );
  }

  if (mode === 'loading') {
    return (
      <div className="flex h-full min-h-[800px] items-center justify-center p-4 text-sm text-gray-600">
        {t('msgDocumentPreviewLoading', { ns: NS })}
      </div>
    );
  }

  if (mode === 'error') {
    return (
      <div className="flex h-full min-h-[800px] items-center justify-center p-4 text-sm text-red-700">
        {errorMessage ?? t('msgDocumentPreviewError', { ns: NS })}
      </div>
    );
  }

  if (mode === 'pdf' && pdfObjectUrl) {
    return (
      <div className="flex h-full min-h-[800px] flex-col">
        <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2">
          {fileName ? (
            <div className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800">{fileName}</div>
          ) : (
            <div className="min-w-0 flex-1" />
          )}
          {onExpandPreview ? (
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1.5 rounded border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
              onClick={onExpandPreview}
            >
              <FaExpand size={11} aria-hidden />
              {t('btnDocumentPreviewExpand', { ns: NS })}
            </button>
          ) : null}
        </div>
        <iframe
          title={fileName ?? t('tabDocuments', { ns: NS })}
          src={pdfObjectUrl}
          className={`min-h-[820px] w-full flex-1 border-0 bg-gray-50 ${
            disableIframePointerEvents ? 'pointer-events-none' : ''
          }`}
        />
      </div>
    );
  }

  if (mode === 'local' && meta) {
    return (
      <div className="flex h-full min-h-[800px] flex-col gap-3 p-4">
        <div>
          <p className="text-sm font-medium text-gray-900">{meta.fileName}</p>
          <p className="mt-1 text-xs text-gray-500">
            {t('msgDocumentOpenLocalHint', { ns: NS })}
          </p>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t('lblUncPath', { ns: NS })}
          </label>
          <code className="block break-all rounded border border-gray-200 bg-gray-50 px-2 py-2 text-xs text-gray-800">
            {meta.uncPath}
          </code>
        </div>
        <div>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
            onClick={handleCopyUnc}
          >
            <FaCopy size={12} />
            {copied ? t('btnUncPathCopied', { ns: NS }) : t('btnCopyUncPath', { ns: NS })}
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default U3121ProjectDocumentPreviewPanel;
